import React from "react";
import { Box, Button, Flex, Link, Text } from "@chakra-ui/react";
import useIdentity from "@/features/ui/hooks/use-identity";
import { signInUtm } from "@/features/landing/constants";
import { getDefaultPageByIdentity } from "@/pages";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { Trans } from "react-i18next";
import useOpenChannelTalk from "@/shared/hooks/useOpenChannelTalk";

interface Props {
  isSignedIn: boolean;
}

// 포털 이미지 위로 얹는 명암 레이어. 상단은 텍스트, 하단은 CTA 가독성을 확보한다.
const SUGGESTION_OVERLAY =
  "linear-gradient(180deg, rgba(8, 13, 22, 0.8) 0%, rgba(0, 0, 0, 0.58) 30%, rgba(0, 0, 0, 0.08) 68%, rgba(0, 0, 0, 0.35) 100%)";

function LandingLastSuggestionSection({ isSignedIn }: Props): JSX.Element {
  const identity = useIdentity();
  const openChannelTalk = useOpenChannelTalk();
  const { tSentence } = useI18nHelper();

  return (
    <Flex
      backgroundColor={"#000000"}
      backgroundImage={`${SUGGESTION_OVERLAY}, url('/images/landing-suggestion-portal.webp')`}
      backgroundRepeat={"no-repeat"}
      backgroundSize={"cover"}
      backgroundPosition={{
        base: "62% 62%",
        md: "center 64%",
      }}
      as={"section"}
      aria-labelledby={"landing-last-suggestion-title"}
      paddingX={{
        base: "16px",
        lg: "24px",
      }}
    >
      <Flex
        maxWidth={"1318px"}
        margin={"0 auto"}
        flexDirection={"column"}
        paddingY={{
          base: "40px",
          lg: "120px ",
          xl: "120px",
        }}
        gap={{
          base: "32px",
          lg: "60px",
        }}
      >
        <Text
          textAlign={"center"}
          fontSize={{
            base: "28px",
            lg: "48px",
          }}
          fontWeight={700}
          lineHeight={{
            base: "38px",
            lg: "68px",
          }}
          color={"#FFFFFF"}
          as={"h2"}
          id={"landing-last-suggestion-title"}
        >
          <Trans
            i18nKey={"landing:suggestion.title"}
            components={{
              br: (
                <Box
                  as={"span"}
                  display={{
                    base: "none",
                    md: "block",
                  }}
                />
              ),
            }}
          />
        </Text>
        <Box paddingY={"80px"}>
          <Text
            fontSize={{
              base: "44px",
              lg: "100px",
            }}
            fontWeight={600}
            color={"#FFFFFF"}
            textAlign={"center"}
          >
            Path{" "}
            <span
              style={{
                color: "rgba(255, 255, 255, 0.40)",
              }}
            >
              + T
            </span>
            rade
          </Text>
        </Box>
        <Flex
          flexDirection={{
            base: "column",
            md: "row",
          }}
          gap={{
            base: "12px",
            lg: "16px",
          }}
          justifyContent={"center"}
        >
          <Link
            padding={{
              base: "12px 24px",
              lg: "16px 32px",
            }}
            borderRadius={"8px"}
            backgroundColor={"#1272EF"}
            color={"#FFFFFF"}
            fontSize={{
              base: "16px",
              lg: "20px",
            }}
            fontWeight={700}
            lineHeight={{
              base: "24px",
              lg: "28px",
            }}
            height={"auto"}
            overflow={"hidden"}
            position={"relative"}
            textAlign={"center"}
            _hover={{
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "#2A2A2A",
                opacity: 0.08,
              },
            }}
            _active={{
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "#2A2A2A",
                opacity: 0.2,
              },
            }}
            href={isSignedIn ? getDefaultPageByIdentity(identity) : signInUtm}
          >
            {isSignedIn
              ? tSentence("landing:enter_service")
              : tSentence("landing:sign_in")}
          </Link>
          <Link
            padding={{
              base: "12px 24px",
              lg: "16px 32px",
            }}
            borderRadius={"8px"}
            border={"1px solid #FFFFFF"}
            fontSize={{
              base: "16px",
              lg: "20px",
            }}
            lineHeight={{
              base: "24px",
              lg: "28px",
            }}
            fontWeight={700}
            color={"#FFFFFF"}
            background={"none"}
            textAlign={"center"}
            height={"auto"}
            as={"button"}
            onClick={openChannelTalk}
            overflow={"hidden"}
            position={"relative"}
            _hover={{
              textDecoration: "none",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "#2A2A2A",
                opacity: 0.08,
              },
              border: "1px solid transparent",
            }}
            _active={{
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: "#2A2A2A",
                opacity: 0.2,
              },
              border: "1px solid transparent",
            }}
          >
            {tSentence("landing:contact_sales")}
          </Link>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default LandingLastSuggestionSection;
